import DOMPurify from 'dompurify';

/**
 * Sanitize SVG content for safe rendering
 * Preserves SVG-specific elements and attributes while removing dangerous content
 */
export function sanitizeSVG(svgContent: string): string {
  return DOMPurify.sanitize(svgContent, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: [
      'svg',
      'g',
      'path',
      'circle',
      'rect',
      'line',
      'polygon',
      'polyline',
      'ellipse',
      'text',
      'tspan',
      'defs',
      'clipPath',
      'use',
      'symbol',
      'style',
    ],
    ADD_ATTR: [
      'viewBox',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-miterlimit',
      'stroke-opacity',
      'fill-opacity',
      'opacity',
      'width',
      'height',
      'd',
      'points',
      'cx',
      'cy',
      'r',
      'rx',
      'ry',
      'x',
      'y',
      'x1',
      'y1',
      'x2',
      'y2',
      'transform',
      'style',
      'class',
      'id',
      'type',
      'xmlns',
      'xmlns:xlink',
      'xlink:href',
      'preserveAspectRatio',
      'font-size',
      'font-family',
      'font-weight',
      'text-anchor',
      'dominant-baseline',
      'clip-path',
      'mask',
      'filter',
      'version',
      'xml:space',
      'enable-background',
    ],
  });
}

/**
 * Sanitize HTML content for safe rendering
 * More restrictive than SVG sanitization
 */
export function sanitizeHTML(htmlContent: string): string {
  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class', 'style'],
  });
}

/**
 * Sanitize content based on type
 * Automatically detects SVG content and applies appropriate sanitization
 */
export function sanitizeContent(content: string, type: 'svg' | 'html' | 'auto' = 'auto'): string {
  if (type === 'svg') {
    return sanitizeSVG(content);
  }

  if (type === 'html') {
    return sanitizeHTML(content);
  }

  // Auto-detect SVG content
  if (content.includes('<svg') || content.includes('xmlns="http://www.w3.org/2000/svg"')) {
    return sanitizeSVG(content);
  }

  return sanitizeHTML(content);
}
